import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, OnDestroy, HostListener } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NgGlyph } from "@ng-icons/core";
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from "rxjs";
import {
  TableColumn,
  TableConfig,
  TableSort,
  TableActionConfig,
  TablePagination,
  TableSearchEvent,
  MultiTableSort,
  DateRangeFilter,
  TableDateRangeEvent,
  TableFilterConfig,
  TableFilterChangeEvent,
  TableSelectionChangeEvent,
  TableEditOption,
} from "./Type/TableType";
import { CdkDrag, CdkDragHandle, CdkDropList } from "@angular/cdk/drag-drop";
import { formatDateNumeric } from "../../Utility/UtilityDate";
import { QtyInputSelectZeroDirective } from "../QtyInput/QtyInputSelectZeroDirective";

@Component({
  selector: "app-table",
  standalone: true,
  imports: [CommonModule, NgGlyph, CdkDrag, CdkDragHandle, CdkDropList, QtyInputSelectZeroDirective],
  templateUrl: "./TableComponent.html",
  styleUrl: "./TableComponent.scss",
})
export class AppTableComponent<T = unknown> implements OnChanges, OnInit, OnDestroy {
  @Input() data: T[] = [];
  @Input() columns: TableColumn<T>[] = [];
  @Input() config: TableConfig<T> = {};
  @Input() pagination: TablePagination = {
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  };

  @Output() sortChange = new EventEmitter<TableSort | null>();
  @Output() pageChange = new EventEmitter<{ page: number; search?: string; filters?: Record<string, string> }>();
  @Output() pageSizeChange = new EventEmitter<{ limit: number; search?: string; filters?: Record<string, string> }>();
  @Output() actionClick = new EventEmitter<{ action: TableActionConfig<T>; item: T }>();
  @Output() searchChange = new EventEmitter<TableSearchEvent>();
  @Output() dateRangeChange = new EventEmitter<TableDateRangeEvent>();
  @Output() filterChange = new EventEmitter<TableFilterChangeEvent>();
  @Output() selectionChange = new EventEmitter<TableSelectionChangeEvent>();

  displayedData: T[] = [];
  currentSort: TableSort | null = null;
  multiSort: MultiTableSort[] = [];
  currentPage = 1;
  currentPageSize = 10;
  searchTerm = "";
  dateRangeFilter: DateRangeFilter = {};
  visibleColumns = new Set<string>();
  showColumnToggle = false;
  showDateRangeFilter = false;
  activeFilters: Record<string, string> = {};
  expandedRowIds = new Set<string>();
  selectedRowIds = new Set<string>();
  readonly pageSizeOptions: number[] = [10, 20, 50, 100];
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isInsideFilter = target.closest(".app-table__date-range-filter-dropdown");
    const isFilterButton = target.closest(".app-table__date-range-filter-btn");

    if (!isInsideFilter && !isFilterButton && this.showDateRangeFilter) {
      this.closeDateRangeFilter();
    }

    const isInsideColumnToggle = target.closest(".app-table__column-toggle-dropdown");
    const isColumnToggleBtn = target.closest(".app-table__column-toggle-btn");

    if (!isInsideColumnToggle && !isColumnToggleBtn && this.showColumnToggle) {
      this.showColumnToggle = false;
    }
  }

  ngOnInit(): void {
    // Set up search debounce
    this.searchSubject
      .pipe(
        debounceTime(300), // 300ms debounce
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((searchTerm) => {
        this.searchChange.emit({ term: searchTerm, page: 1, filters: { ...this.activeFilters } }); // Always reset to page 1 on search
      });

    // Initialize visible columns - use visibleByDefault property if available, otherwise show all
    this.columns.forEach((column) => {
      if (column.visibleByDefault !== false) {
        this.visibleColumns.add(column.key);
      }
    });

    this.initializeFilters();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"] || changes["currentSort"]) {
      this.applySorting();
      this.pruneExpandedRows();
      this.syncSelectionFromConfig();
    }
    if (changes["pagination"]) {
      this.currentPage = this.pagination.page;
      this.currentPageSize = this.pagination.limit;
    }
    if (changes["config"]) {
      this.syncSelectionFromConfig();
    }
  }

  /**
   * Handles search input changes and updates the search term.
   * Triggers a debounced search event.
   * @param event - The input event from the search field.
   */
  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.searchSubject.next(target.value);
  }

  get filters(): TableFilterConfig[] {
    return this.config.filters ?? [];
  }

  hasFilters(): boolean {
    return this.filters.length > 0;
  }

  onFilterInput(key: string, event: Event): void {
    const target = event.target as HTMLSelectElement;
    const nextValue = target.value ?? "";
    this.activeFilters[key] = nextValue;
    this.filterChange.emit({
      filters: { ...this.activeFilters },
      page: 1,
      term: this.searchTerm || undefined,
    });
  }

  getCellClass(column: TableColumn<T>, item: T): string {
    if (!column.cellClass) {
      return "";
    }
    if (typeof column.cellClass === "function") {
      return column.cellClass(item);
    }
    return column.cellClass;
  }

  getCellIcon(column: TableColumn<T>, item: T): string | null {
    if (!column.cellIcon) {
      return null;
    }
    if (typeof column.cellIcon === "function") {
      return column.cellIcon(item);
    }
    return column.cellIcon;
  }

  hasInlineEdit(): boolean {
    return this.config.inlineEdit?.enabled === true;
  }

  isRowEditing(item: T): boolean {
    if (!this.hasInlineEdit()) {
      return false;
    }
    return this.config.inlineEdit?.isRowEditing?.(item) ?? false;
  }

  isColumnEditable(column: TableColumn<T>, item: T): boolean {
    if (!this.hasInlineEdit()) {
      return false;
    }

    if (typeof column.editable === "function") {
      return column.editable(item);
    }

    return column.editable === true;
  }

  isInlineEditableCell(column: TableColumn<T>, item: T): boolean {
    return this.isRowEditing(item) && this.isColumnEditable(column, item);
  }

  getDraftCellValue(item: T, column: TableColumn<T>): string | number | null {
    if (!this.hasInlineEdit()) {
      return column.editValue?.(item) ?? null;
    }

    const draftValue = this.config.inlineEdit?.getDraftValue(item, column);
    if (draftValue !== undefined) {
      return draftValue ?? null;
    }

    return column.editValue?.(item) ?? null;
  }

  setDraftCellValue(item: T, column: TableColumn<T>, event: Event): void {
    if (!this.hasInlineEdit()) {
      return;
    }

    const target = event.target as HTMLInputElement | HTMLSelectElement;
    const nextRawValue = target.value;
    const nextValue = (() => {
      if (column.editType === "number") {
        return (() => {
          if (nextRawValue === "") {
            return null;
          }
          return Number(nextRawValue);
        })();
      }
      return nextRawValue;
    })();

    this.config.inlineEdit?.setDraftValue(item, column, nextValue);
  }

  getEditOptions(column: TableColumn<T>, item: T): TableEditOption[] {
    if (!column.editOptions) {
      return [];
    }

    return (() => {
      if (typeof column.editOptions === "function") {
        return column.editOptions(item);
      }
      return column.editOptions;
    })();
  }

  getHeaderContentJustify(column: TableColumn<T>): string {
    if (column.align === "right") {
      return "flex-end";
    }
    if (column.align === "center") {
      return "center";
    }
    return "flex-start";
  }

  hasExpandableRows(): boolean {
    return !!this.config.expandable?.enabled;
  }

  hasSelectableRows(): boolean {
    return !!this.config.selection?.enabled;
  }

  getSelectableRowId(item: T): string {
    return this.config.selection?.getRowId(item) ?? "";
  }

  isRowSelectable(item: T): boolean {
    if (!this.hasSelectableRows()) {
      return false;
    }

    return this.config.selection?.selectable?.(item) ?? true;
  }

  isRowSelected(item: T): boolean {
    return this.selectedRowIds.has(this.getSelectableRowId(item));
  }

  toggleRowSelection(item: T, event?: Event): void {
    event?.stopPropagation();
    if (!this.isRowSelectable(item)) {
      return;
    }

    const rowId = this.getSelectableRowId(item);
    if (!rowId) {
      return;
    }

    if (this.selectedRowIds.has(rowId)) {
      this.selectedRowIds.delete(rowId);
    } else {
      this.selectedRowIds.add(rowId);
    }

    this.emitSelectionChange();
  }

  toggleSelectAllVisible(event: Event): void {
    const target = event.target as HTMLInputElement;
    const shouldSelect = target.checked;
    const selectableRowIds = this.displayedData
      .map((item) => this.getSelectableRowId(item))
      .filter((rowId, index) => {
        const item = this.displayedData[index];
        return !!rowId && this.isRowSelectable(item);
      });

    if (shouldSelect) {
      selectableRowIds.forEach((rowId) => this.selectedRowIds.add(rowId));
    } else {
      selectableRowIds.forEach((rowId) => this.selectedRowIds.delete(rowId));
    }

    this.emitSelectionChange();
  }

  areAllVisibleRowsSelected(): boolean {
    const selectableItems = this.displayedData.filter((item) => this.isRowSelectable(item));
    if (selectableItems.length === 0) {
      return false;
    }

    return selectableItems.every((item) => this.selectedRowIds.has(this.getSelectableRowId(item)));
  }

  hasSomeVisibleRowsSelected(): boolean {
    const selectableItems = this.displayedData.filter((item) => this.isRowSelectable(item));
    if (selectableItems.length === 0) {
      return false;
    }

    const selectedCount = selectableItems.filter((item) => this.selectedRowIds.has(this.getSelectableRowId(item))).length;
    return selectedCount > 0 && selectedCount < selectableItems.length;
  }

  getExpandButtonLabel(): string {
    return this.config.expandable?.expandButtonLabel || "Toggle row details";
  }

  getRowId(item: T): string {
    return this.config.expandable?.getRowId(item) ?? "";
  }

  isRowExpanded(item: T): boolean {
    if (!this.hasExpandableRows()) {
      return false;
    }

    return this.expandedRowIds.has(this.getRowId(item));
  }

  toggleRowExpanded(item: T, event?: Event): void {
    event?.stopPropagation();

    if (!this.hasExpandableRows()) {
      return;
    }

    const rowId = this.getRowId(item);
    if (!rowId) {
      return;
    }

    if (this.expandedRowIds.has(rowId)) {
      this.expandedRowIds.delete(rowId);
      return;
    }

    if (this.config.expandable?.allowMultipleExpanded === false) {
      this.expandedRowIds.clear();
    }

    this.expandedRowIds.add(rowId);
  }

  getColumnSpan(): number {
    return (
      this.getVisibleColumns().length +
      (() => {
        if (this.config.showActions) {
          return 1;
        }
        return 0;
      })() +
      (() => {
        if (this.hasExpandableRows()) {
          return 1;
        }
        return 0;
      })() +
      (() => {
        if (this.hasSelectableRows()) {
          return 1;
        }
        return 0;
      })()
    );
  }

  /**
   * Triggers the create action if configured.
   */
  onCreateClick(): void {
    if (this.config.onCreate) {
      this.config.onCreate();
    }
  }

  /**
   * Toggles the visibility of a column.
   * @param key - The column key to toggle.
   */
  toggleColumnVisibility(key: string): void {
    if (this.visibleColumns.has(key)) {
      this.visibleColumns.delete(key);
    } else {
      this.visibleColumns.add(key);
    }
  }

  /**
   * Checks if a column is currently visible.
   * @param key - The column key to check.
   * @returns True if the column is visible, false otherwise.
   */
  isColumnVisible(key: string): boolean {
    return this.visibleColumns.has(key);
  }

  /**
   * Toggles the column visibility dropdown.
   */
  toggleColumnToggle(): void {
    this.showColumnToggle = !this.showColumnToggle;
  }

  /**
   * Toggles the date range filter dropdown.
   */
  toggleDateRangeFilter(): void {
    this.showDateRangeFilter = !this.showDateRangeFilter;
  }

  /**
   * Closes the date range filter dropdown.
   */
  closeDateRangeFilter(): void {
    this.showDateRangeFilter = false;
  }

  /**
   * Handles date range filter change and emits the new date range with current page.
   * @param event - The change event from the date input.
   * @param type - The type of date range input (from or to).
   */
  onDateRangeChange(event: Event, type: "from" | "to"): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    if (type === "from") {
      this.dateRangeFilter.from = value || undefined;
    } else {
      this.dateRangeFilter.to = value || undefined;
    }

    this.dateRangeChange.emit({
      dateRange: { ...this.dateRangeFilter },
      page: 1,
    });
  }

  /**
   * Clears the date range filter and emits empty date range.
   */
  clearDateRangeFilter(): void {
    this.dateRangeFilter = {};
    this.dateRangeChange.emit({
      dateRange: {},
      page: 1,
    });
  }

  /**
   * Checks if date range filter is currently active.
   * @returns True if either from or to date is set.
   */
  hasDateRangeFilter(): boolean {
    return !!(this.dateRangeFilter.from || this.dateRangeFilter.to);
  }

  /**
   * Formats a date string to display format for the date range filter button.
   * @param dateString - The ISO date string to format.
   * @returns Formatted date string or empty string if not provided.
   */
  formatDateForDisplay(dateString?: string): string {
    if (!dateString) return "";
    return formatDateNumeric(dateString);
  }

  /**
   * Gets all currently visible columns.
   * @returns Array of visible column definitions.
   */
  getVisibleColumns(): TableColumn<T>[] {
    return this.columns.filter((column) => this.isColumnVisible(column.key));
  }

  getActionsWidth(): string {
    return this.config.actionsWidth || "120px";
  }

  /**
   * Applies sorting to the displayed data based on current multi-sort configuration.
   * Sorts by priority (lower priority number = higher priority).
   */
  private applySorting(): void {
    if (this.multiSort.length === 0) {
      this.displayedData = [...this.data];
      return;
    }

    // Sort by priority (lower priority number means higher priority)
    const sortedMultiSort = [...this.multiSort].sort((a, b) => a.priority - b.priority);

    this.displayedData = [...this.data].sort((a, b) => {
      for (const sort of sortedMultiSort) {
        const aValue = this.getNestedValue(a, sort.key);
        const bValue = this.getNestedValue(b, sort.key);

        let comparison = 0;

        // Handle different data types for sorting
        if (typeof aValue === "string" && typeof bValue === "string") {
          comparison = aValue.localeCompare(bValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          comparison = aValue - bValue;
        } else {
          // Convert to string for comparison
          comparison = String(aValue).localeCompare(String(bValue));
        }

        if (comparison !== 0) {
          return (() => {
            if (sort.direction === "asc") {
              return comparison;
            }
            return -comparison;
          })();
        }
        // If values are equal, continue to next sort criterion
      }
      return 0; // All sort criteria are equal
    });
  }

  /**
   * Gets a nested value from an object using a dot-notation path.
   * @param obj - The object to extract the value from.
   * @param path - The dot-notation path (e.g., "user.profile.name").
   * @returns The value at the specified path, or undefined if not found.
   */
  getNestedValue(obj: T, path: string): unknown {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return path.split(".").reduce((current: any, key: string) => current?.[key], obj as any);
  }

  /**
   * Handles column sort click. Supports single and multi-column sorting.
   * - Click: Single column sort (toggles asc/desc)
   * - Shift+Click: Add column to multi-sort
   * @param column - The column definition being sorted.
   * @param event - The mouse event that triggered the sort.
   */
  onSort(column: TableColumn<T>, event: MouseEvent): void {
    if (!column.sortable) return;

    const isShiftKey = event.shiftKey;

    if (isShiftKey) {
      // Multi-column sorting
      const existingSortIndex = this.multiSort.findIndex((s) => s.key === column.key);

      if (existingSortIndex >= 0) {
        // Toggle direction of existing sort
        const existingSort = this.multiSort[existingSortIndex];
        const newDirection = (() => {
          if (existingSort.direction === "asc") {
            return "desc";
          }
          return "asc";
        })();
        this.multiSort[existingSortIndex] = { ...existingSort, direction: newDirection };
      } else {
        // Add new sort with next priority
        const newPriority = (() => {
          if (this.multiSort.length > 0) {
            return Math.max(...this.multiSort.map((s) => s.priority)) + 1;
          }
          return 1;
        })();
        this.multiSort.push({ key: column.key, direction: "asc", priority: newPriority });
      }
    } else {
      // Single column sorting - replace all sorts
      if (this.multiSort.length === 1 && this.multiSort[0].key === column.key) {
        // Toggle direction if clicking the same column
        const newDirection = (() => {
          if (this.multiSort[0].direction === "asc") {
            return "desc";
          }
          return "asc";
        })();
        this.multiSort = [{ key: column.key, direction: newDirection, priority: 1 }];
      } else {
        // Set new sort column with ascending direction
        this.multiSort = [{ key: column.key, direction: "asc", priority: 1 }];
      }
    }

    this.applySorting();

    // Emit the current primary sort for backward compatibility
    const primarySort = (() => {
      if (this.multiSort.length > 0) {
        return { key: this.multiSort[0].key, direction: this.multiSort[0].direction };
      }
      return null;
    })();
    this.sortChange.emit(primarySort);
  }

  /**
   * Gets the sort icon name for a column based on current sort state.
   * @param column - The column definition.
   * @returns The icon name: "sort", "arrow_upward", or "arrow_downward".
   */
  getSortIcon(column: TableColumn<T>): string {
    const sortIndex = this.multiSort.findIndex((s) => s.key === column.key);
    if (sortIndex === -1) {
      return "sort";
    }

    const sort = this.multiSort[sortIndex];
    return (() => {
      if (sort.direction === "asc") {
        return "arrow_upward";
      }
      return "arrow_downward";
    })();
  }

  /**
   * Gets the sort priority number for a column in multi-sort mode.
   * @param column - The column definition.
   * @returns The priority number (1-based), or null if not sorted.
   */
  getSortPriority(column: TableColumn<T>): number | null {
    const sortIndex = this.multiSort.findIndex((s) => s.key === column.key);
    if (sortIndex === -1) {
      return null;
    }
    return this.multiSort[sortIndex].priority;
  }

  /**
   * Handles page change and emits the new page number with current search term.
   * @param page - The new page number to navigate to.
   */
  onPageChange(page: number): void {
    if (page >= 1 && page <= this.pagination.pages && page !== this.currentPage) {
      this.currentPage = page;
      this.pageChange.emit({ page, search: this.searchTerm || undefined, filters: { ...this.activeFilters } });
    }
  }

  /**
   * Handles page size change and emits the new limit with current search term.
   * Resets to page 1 when page size changes.
   * @param event - The change event from the page size select.
   */
  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const newSize = parseInt(target.value, 10);

    if (Number.isNaN(newSize) || newSize === this.pagination.limit) {
      return;
    }

    this.currentPage = 1;
    this.currentPageSize = newSize;
    this.pageSizeChange.emit({ limit: newSize, search: this.searchTerm || undefined, filters: { ...this.activeFilters } });
  }

  /**
   * Handles action button click and emits the action with the item.
   * @param action - The action configuration.
   * @param item - The data item the action was clicked on.
   */
  onActionClick(action: TableActionConfig<T>, item: T): void {
    if (action.handler) action.handler(item);

    this.actionClick.emit({ action, item });
  }

  /**
   * Gets the range of page numbers to display in pagination controls.
   * Shows current page ±2 pages, bounded by total pages.
   * @returns Array of page numbers to display.
   */
  getPaginationRange(): number[] {
    const range = [];
    const start = Math.max(1, this.currentPage - 2);
    const end = Math.min(this.pagination.pages, this.currentPage + 2);

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  /**
   * Gets the end index of the current page for display in pagination info.
   * @returns The end index (capped at total items).
   */
  getEndRange(): number {
    return Math.min(this.pagination.page * this.pagination.limit, this.pagination.total);
  }

  private initializeFilters(): void {
    const nextFilters: Record<string, string> = {};
    for (const filter of this.filters) {
      nextFilters[filter.key] = filter.defaultValue ?? "";
    }
    this.activeFilters = nextFilters;
  }

  private syncSelectionFromConfig(): void {
    const configuredIds = this.config.selection?.selectedRowIds ?? [];
    this.selectedRowIds = new Set(configuredIds);
  }

  private emitSelectionChange(): void {
    this.selectionChange.emit({ selectedRowIds: Array.from(this.selectedRowIds) });
  }

  private pruneExpandedRows(): void {
    if (!this.hasExpandableRows()) {
      this.expandedRowIds.clear();
      return;
    }

    const validIds = new Set(this.displayedData.map((item) => this.getRowId(item)).filter((rowId) => !!rowId));
    this.expandedRowIds.forEach((rowId) => {
      if (!validIds.has(rowId)) {
        this.expandedRowIds.delete(rowId);
      }
    });
  }

  /**
   * Handles column drag-and-drop reordering.
   * Updates the column order while preserving visibility state.
   * @param event - The drop event with previous and current index.
   */
  onColumnDrop(event: { previousIndex: number; currentIndex: number }): void {
    const previousIndex = event.previousIndex;
    const currentIndex = event.currentIndex;

    // Get the current visible columns in order
    const currentVisibleColumns = this.getVisibleColumns();

    // Get the column that was moved
    const movedColumn = currentVisibleColumns[previousIndex];

    // Remove the column from its old position
    currentVisibleColumns.splice(previousIndex, 1);

    // Insert the column at its new position
    currentVisibleColumns.splice(currentIndex, 0, movedColumn);

    // Create a new columns array with the reordered visible columns
    // First, get all hidden columns (preserving their original order)
    const hiddenColumns = this.columns.filter((col) => !this.visibleColumns.has(col.key));

    // Combine reordered visible columns with hidden columns
    const newColumnOrder = [...currentVisibleColumns, ...hiddenColumns];

    // Update the columns array while preserving visibility state
    this.columns = newColumnOrder;

    // Reapply sorting after column reordering
    this.applySorting();
  }

  protected readonly Array = Array;
}
